import type * as React from "react";

import { CheckIcon, CrossIcon } from "./Icons";
import { cn } from "../lib/cn";

export type CheckState = "open" | "done" | "asking" | "declined";

const stateBox: Record<CheckState, string> = {
  open: "border-rule-strong bg-card",
  done: "border-ink-recorded bg-fill-recorded",
  asking: "border-ink-attention bg-fill-attention",
  declined: "border-ink-declined bg-fill-declined",
};

const stateLabel: Record<CheckState, string> = {
  open: "Not done",
  done: "Done",
  asking: "Brian is asking",
  declined: "Declined",
};

const BOX = cn(
  "inline-flex size-7 shrink-0 items-center justify-center rounded-sheet border-[1.5px]",
  "font-mono text-base font-semibold text-ink",
);

interface StateCheckboxBaseProps {
  state: CheckState;
  label?: string;
  className?: string;
}

interface StaticStateCheckboxProps
  extends
    StateCheckboxBaseProps,
    Omit<React.HTMLAttributes<HTMLSpanElement>, "children" | "onToggle"> {
  onToggle?: undefined;
  ref?: React.Ref<HTMLSpanElement>;
}

interface PressableStateCheckboxProps
  extends
    StateCheckboxBaseProps,
    Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "children" | "onClick" | "type" | "role" | "onToggle"
    > {
  /** Only open and done toggle; asking and declined are read-only. */
  onToggle: (next: CheckState) => void;
  ref?: React.Ref<HTMLButtonElement>;
}

export type StateCheckboxProps =
  | StaticStateCheckboxProps
  | PressableStateCheckboxProps;

function StateGlyph({ state }: { state: CheckState }) {
  if (state === "done")
    return <CheckIcon strokeWidth={3} className="text-base" />;
  if (state === "declined")
    return <CrossIcon strokeWidth={3} className="text-base" />;
  if (state === "asking") return <>?</>;
  return null;
}

export function StateCheckbox(props: StateCheckboxProps) {
  const name = props.label ?? stateLabel[props.state];

  if (
    props.onToggle != null &&
    (props.state === "open" || props.state === "done")
  ) {
    const {
      state,
      label: _label,
      onToggle,
      className,
      disabled,
      ...rest
    } = props;
    return (
      <button
        {...rest}
        type="button"
        role="checkbox"
        aria-checked={state === "done"}
        aria-label={name}
        disabled={disabled}
        onClick={() => onToggle(state === "open" ? "done" : "open")}
        className={cn(
          BOX,
          stateBox[state],
          "transition duration-150 ease-paper",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "enabled:cursor-pointer enabled:hover:shadow-lift disabled:cursor-default",
          className,
        )}
      >
        <StateGlyph state={state} />
      </button>
    );
  }

  const {
    state,
    label: _label,
    onToggle: _onToggle,
    className,
    ...rest
  } = props as StaticStateCheckboxProps;
  return (
    <span
      role="img"
      aria-label={name}
      {...rest}
      className={cn(BOX, stateBox[state], className)}
    >
      <StateGlyph state={state} />
    </span>
  );
}
